import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { hoverVariant } from '../utils/buttonMotion';
import { Phone, Toolbox } from '@phosphor-icons/react';

export default function HeroSection({ darkMode, callNumber }) {
  const reduce = useReducedMotion();
  const allowMotion = (config) => (reduce ? undefined : config);
  
  return (
    <section className="relative px-4 md:px-6 overflow-hidden pt-24 md:pt-32 pb-16 md:pb-24 min-h-[80vh] md:min-h-[85vh] lg:min-h-[80vh] scroll-mt-24" id="home" role="main" aria-labelledby="hero-title">
      <picture
        className="pointer-events-none absolute inset-0 z-0"
      >
        <source
          srcSet={`${process.env.PUBLIC_URL}/assets/hero-section-pc-repair-just-one.webp`}
          type="image/webp"
        />
        <img
          src={`${process.env.PUBLIC_URL}/assets/hero-section-pc-repair-just-one.png`}
          alt=""
          aria-hidden="true"
          loading="eager"
          className="w-full h-full object-cover"
          style={{
            opacity: darkMode ? 0.45 : 0.75,
            filter: darkMode ? 'saturate(1.10) contrast(1.08)' : 'brightness(0.92) saturate(1.05)',
          }}
          draggable={false}
        />
      </picture>
      <div className="max-w-6xl mx-auto text-center relative z-10">
        <motion.div
          initial={allowMotion({ opacity: 0, y: 50 })}
          animate={allowMotion({ opacity: 1, y: 0 })}
          transition={{ duration: 1, ease: "easeOut" }}
          className="max-w-5xl mx-auto"
        >
          <h1 id="hero-title" className={`${darkMode ? "text-white" : "text-white [text-shadow:_0_2px_8px_rgb(0_0_0_/80%),_0_4px_16px_rgb(0_0_0_/60%)]"} text-3xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight`}>
            Expert Computer Repair in<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 [text-shadow:none]">Rosario, Batangas</span>
          </h1>
          <motion.p
            className={`text-base md:text-2xl mb-6 md:mb-8 leading-relaxed px-2 ${darkMode ? "text-gray-300" : "text-white font-bold [text-shadow:_0_2px_8px_rgb(0_0_0_/90%),_0_4px_12px_rgb(0_0_0_/70%)]"}`}
            initial={allowMotion({ opacity: 0, y: 30 })}
            animate={allowMotion({ opacity: 1, y: 0 })}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Professional PC repair, laptop fixes, gaming PC builds & more.<br className="hidden md:block" /> <span className="font-bold text-orange-400">✓ No Fix, No Fee</span> <span className="font-bold text-orange-400">✓ Same Day Service</span>
          </motion.p>
          
          <motion.div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 justify-center items-center px-2"
            initial={allowMotion({ opacity: 0, y: 30 })}
            animate={allowMotion({ opacity: 1, y: 0 })}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.button
              onClick={() => callNumber("09202308881")}
              className={`w-full sm:w-auto px-4 sm:px-6 md:px-10 py-3 sm:py-4 md:py-5 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white rounded-lg md:rounded-xl font-bold text-base sm:text-lg md:text-xl border-2 transition-all duration-100 hover:bg-gradient-to-tl flex items-center justify-center gap-2 md:gap-3 relative overflow-hidden group whitespace-nowrap ${
                darkMode 
                  ? 'shadow-2xl border-orange-300/60 hover:shadow-orange-500/25 hover:shadow-xl hover:border-orange-200' 
                  : 'shadow-[0_8px_30px_rgb(251,146,60,0.5)] border-orange-300 hover:shadow-[0_12px_40px_rgb(251,146,60,0.7)] hover:border-orange-200 ring-2 ring-orange-400/30'
              }`}
              {...hoverVariant({ whileHover: { scale: 1.02, y: -2 } }, reduce)}
            >
              {/* Elegant Animated Stars Around Button */}
              {!reduce && (
                <div className="absolute inset-0 pointer-events-none">
                  {/* Star 1 - Top Left Corner */}
                  <motion.div
                    className="absolute text-orange-200/80 text-xs"
                    style={{ top: '20%', left: '15%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: 0,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>

                  {/* Star 2 - Top Center */}
                  <motion.div
                    className="absolute text-yellow-200/70 text-xs"
                    style={{ top: '25%', left: '45%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.5, 1.2, 0.5]
                    }}
                    transition={{
                      duration: 3.5,
                      repeat: Infinity,
                      delay: 0.7,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>

                  {/* Star 3 - Top Right Corner */}
                  <motion.div
                    className="absolute text-orange-300/60 text-xs"
                    style={{ top: '20%', left: '80%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.3, 0.8, 0.3]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      delay: 1.4,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>

                  {/* Star 4 - Middle Left */}
                  <motion.div
                    className="absolute text-yellow-300/50 text-xs"
                    style={{ top: '50%', left: '10%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.4, 1, 0.4]
                    }}
                    transition={{
                      duration: 3.2,
                      repeat: Infinity,
                      delay: 2.1,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>

                  {/* Star 5 - Middle Right */}
                  <motion.div
                    className="absolute text-orange-100/90 text-xs"
                    style={{ top: '50%', left: '85%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.6, 1.1, 0.6]
                    }}
                    transition={{
                      duration: 4.2,
                      repeat: Infinity,
                      delay: 2.8,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>

                  {/* Star 6 - Bottom Left Corner */}
                  <motion.div
                    className="absolute text-yellow-200/80 text-xs"
                    style={{ top: '75%', left: '20%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.4, 0.9, 0.4]
                    }}
                    transition={{
                      duration: 3.8,
                      repeat: Infinity,
                      delay: 3.5,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>

                  {/* Star 7 - Bottom Right Corner */}
                  <motion.div
                    className="absolute text-orange-200/70 text-xs"
                    style={{ top: '75%', left: '75%' }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 4.5,
                      repeat: Infinity,
                      delay: 4.2,
                      ease: "easeInOut"
                    }}
                  >
                    ✦
                  </motion.div>
                </div>
              )}
              
              <Phone size={24} weight="fill" className="text-orange-100 drop-shadow-lg relative z-10 md:hidden" />
              <Phone size={28} weight="fill" className="text-orange-100 drop-shadow-lg relative z-10 hidden md:inline" />
              <span className="relative z-10 drop-shadow-lg">Call 0920-230-8881</span>
            </motion.button>
            <motion.button
              onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
              className={`w-full sm:w-auto ${darkMode ? 'bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 hover:bg-gradient-to-br hover:from-slate-700 hover:via-slate-600 hover:to-slate-700 text-orange-400 hover:text-orange-400/90' : 'bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 hover:bg-gradient-to-tl hover:from-blue-100 hover:via-blue-200 hover:to-blue-100 text-orange-600 hover:text-orange-700 font-semibold'} px-4 sm:px-6 md:px-8 py-3 sm:py-3 md:py-4 rounded-lg md:rounded-xl font-medium text-base sm:text-lg md:text-xl border ${darkMode ? 'border-slate-600/50 hover:border-slate-600/50' : 'border-blue-300 hover:border-blue-400'} transition-all duration-200 hover:shadow-md ${darkMode ? 'hover:shadow-orange-500/15' : 'hover:shadow-orange-500/15'} flex items-center justify-center gap-2 md:gap-3 shadow-inner whitespace-nowrap`}
              {...hoverVariant({ whileHover: { scale: 1.01, y: -1 } }, reduce)}
            >
              <Toolbox size={20} weight="fill" className={`${darkMode ? 'text-orange-200' : 'text-orange-400'} md:hidden`} />
              <Toolbox size={24} weight="fill" className={`${darkMode ? 'text-orange-200' : 'text-orange-400'} hidden md:inline`} />
              View Our Work
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
